import React, { useEffect, useState } from 'react'
import useTakeBackStyle from '../../components/TakeBack/TakeBack.style'
import { scrollToDiv } from '../../utils/utils'
import { Autocomplete } from '../Input/AutoComplete'
import { getAlgoliaResults } from '@algolia/autocomplete-js'
import algoliasearch from 'algoliasearch'
import { useTranslation } from 'react-i18next'
import clsx from 'clsx'

interface LocalizedAttributes {
  attributes: string[]
  product_name: string
  taxon_names: string[]
  variant_names: string[]
}

// Need to be updated with new locale [fr_FR, en_US, de_DE]
type Localized<T> = {
  [P in keyof T as `${string & P}_en_US`]: T[P]
} & {
  [P in keyof T as `${string & P}_fr_FR`]: T[P]
} & {
  [P in keyof T as `${string & P}_de_DE`]: T[P]
}

export interface ProductSearch extends Localized<LocalizedAttributes> {
  customer_id: number
  image_path: string
  objectID: string
  product_code: string
  product_id: number
  product_label_reference: string
  variants: any[]
}

type Tprops = {
  showCriterion: boolean
  label: any
  handleCheckProduct: (productId: number) => void
  lang?: string
}

type ProductItemProps = {
  item: ProductSearch
  components: any
  onClick: (item: ProductSearch, setQuery: (query: string) => void) => void
  setQuery: (query: string) => void
}

const AutoCompleteTakeBack = ({
  showCriterion,
  label,
  handleCheckProduct,
  lang,
}: Tprops) => {
  const { t } = useTranslation()
  const classes = useTakeBackStyle()
  const locale = lang.slice(0, 2) + '_' + lang.slice(3).toUpperCase()
  useEffect(() => {
    showCriterion && scrollToDiv('takeBack__nav')
  }, [showCriterion])
  const [searchQuery, setSearchQuery] = useState('')
  const searchClient = algoliasearch(
    process.env.ALGOLIA_APP_ID,
    process.env.ALGOLIA_API_KEY
  )

  const ProductItem = ({
    item,
    components,
    onClick,
    setQuery,
  }: ProductItemProps) => {
    const handleClick = () => {
      onClick(item, setQuery)
      setQuery(item[`product_name_${locale}`])
    }

    return (
      <div onClick={handleClick} className={classes.itemRoot}>
        <div className={classes.itemIcon}>
          {item.image_path && (
            <img src={item.image_path} alt={item[`product_name_${locale}`]} />
          )}
        </div>
        <div className={classes.itemContent}>
          <div className={classes.itemTitle}>
            <components.Highlight
              hit={item}
              attribute={`product_name_${locale}`}
            />
          </div>
          <div className={classes.itemLabelReference}>
            <components.Highlight
              hit={item}
              attribute={'product_label_reference'}
            />
          </div>
        </div>
      </div>
    )
  }

  const handleProductClick = (item: ProductSearch, setQuery) => {
    handleCheckProduct(item.product_id)
    setSearchQuery(item[`product_name_${locale}`])
    setQuery(item[`product_name_${locale}`])
  }

  return (
    <>
      <label className={clsx('label', classes.labelAutoComplete)}>
        <>{label}</>
      </label>
      <Autocomplete
        placeholder={t('texts:general.search_bar.input_placeholder')}
        initialState={{ query: searchQuery || '' }}
        getSources={({ query, setQuery }) => [
          {
            sourceId: 'products',
            insights: true,
            getItems() {
              return getAlgoliaResults({
                searchClient,
                queries: [
                  {
                    indexName: process.env.ALGOLIA_INDICE,
                    query,
                    params: {
                      filters: 'has_product_label_reference:true',
                    },
                  },
                ],
              })
            },
            templates: {
              header() {
                return <></>
              },
              item({ item, components }) {
                return (
                  <ProductItem
                    item={item}
                    components={components}
                    onClick={handleProductClick}
                    setQuery={setQuery}
                  />
                )
              },
            },
          },
        ]}
        classNames={{
          // documentation to know classNames : https://www.algolia.com/doc/ui-libraries/autocomplete/api-reference/autocomplete-js/autocomplete/#param-classnames
          form: 'takeback__input_form',
          input: 'takeback__input',
        }}
      />
    </>
  )
}

export default AutoCompleteTakeBack
